import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";

import { Button, Descriptions, Form, PageHeader, Input, Select, Table, Modal, Typography } from "antd";
import { PrinterOutlined, SearchOutlined } from "@ant-design/icons";
import ReactQuill from 'react-quill';
import _ from "lodash"; //library used to encoding/decoding html entities

import notifier from "utils/notifier";
import { ErrorResponse } from "utils/types";
import { formatDate, getEnumKeyByEnumValue } from "utils/helpers";
import { PacStatus, NoteAction, UserRole } from "utils/enums";

import DatePickerLocalised from 'components/Common/DatePickerLocalised';

import PostAcuteCareClinicalPlanDetailProps from './Proptypes';

import "./index.scss";
import "react-quill/dist/quill.snow.css";
import "react-quill/dist/quill.bubble.css";

const { Title } = Typography;
const { Option } = Select;


const PostAcuteCareClinicalPlanDetail = (props: PostAcuteCareClinicalPlanDetailProps) => {
  const history = useHistory();

  const [form] = Form.useForm();
  const [carePlan, setCarePlan] = useState<any[]>([]);
  const [description, setDescription] = useState('');
  const [dischargeSummary, setDischargeSummary] = useState('');
  const [reconciliation, setReconciliation] = useState('');
  const [noteBy, setNoteBy] = useState('');
  const [noteDateTime, setNoteDateTime] = useState('');
  const [mostRecentHospitalizeDate, setMostRecentHospitalizeDate] = useState('');
  const [status, setStatus] = useState('');
  const [pacpMedication, setPacpMedication] = useState<any[]>([]);
  const [isPlanLogVisible, setIsPlanLogVisible] = useState(false);
  const [noteLogsOneDataSource, setNoteLogsOneDataSource] = useState<any[]>([]);
  const [noteLogsTwoDataSource, setNoteLogsTwoDataSource] = useState<any[]>([]);

  useEffect(() => {
    getDetailPlan();
    getMedDetail();
  }, []);

  //modules tools setup for the react-quill text editor
  const modules =  {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      [{ 'background': [] }],
    ]
  }

  //formats setup for the react-quill text editor
  const formats = [
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'background'
  ];

  const columns = [
    {
      title: "No.",
      key: "RmiSequence",
      dataIndex: "RmiSequence",
    },
    {
      title: "Assessment",
      key: "RmiAssessment",
      dataIndex: "RmiAssessment",
      render: (__: string, record: any, ___: number) => {
        return (
          <ReactQuill
            className="mt-2"
            theme={"bubble"}
            readOnly={true}
            value={_.unescape(record.RmiAssessment)}
          />
        )
      }
    },
    {
      title: "Plan",
      key: "RmiPlan",
      dataIndex: "RmiPlan",
      render: (__: string, record: any, ___: number) => {
        return (
          <ReactQuill
            className="mt-2"
            theme={"bubble"}
            readOnly={true}
            value={_.unescape(record.RmiPlan)}
          />
        )
      }
    },
  ];

  const noteLogColumnsOne = [
    {
      title: "Date",
      key: "RmiLogDate",
      dataIndex: "RmiLogDate",
      render: (_: string, record: any, __: number) =>  formatDate(record.RmiLogDate, 'MM/DD/YYYY')
    },
    {
      title: "Changed By",
      key: "RmiLogUser",
      dataIndex: "RmiLogUser",
    },
    {
      title: "Field",
      key: "RmiAttribute",
      dataIndex: "RmiAttribute",
      render: (_: string, record: any, __: number) => record.RmiAttribute.replace(/Rmi/g, '')
    },
    {
      title: "Action",
      key: "RmiAction",
      dataIndex: "RmiAction",
      render: (_: string, record: any, __: number) => NoteAction[record.RmiAction]
    },
    {
      title: "New Value",
      key: "RmiNewValue",
      dataIndex: "RmiNewValue",
    },
    {
      title: "Old Value",
      key: "RmiOldValue",
      dataIndex: "RmiOldValue",
    },
  ];

  const noteLogColumnsTwo = [
    {
      title: "Date",
      key: "RmiAccessDate",
      dataIndex: "RmiAccessDate",
      render: (_: string, record: any, __: number) =>  formatDate(record.RmiAccessDate, 'MM/DD/YYYY')
    },
    {
      title: "Accessed By",
      key: "RmiAccessBy",
      dataIndex: "RmiAccessBy"
    }
  ];

  const getMedDetail = () => {
    if (props.planId === 0) { //planId is 0, then it's creating a new note
      return;
    } else {
      return props.getMedDetail(props.planId)
        .then((response: any[]) => {
          setPacpMedication(response);
        })
        .catch((err: ErrorResponse) => {
          notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
        });
    }
  }

  const getPlanLogs = () => {
    if (props.planId === 0) {
      return notifier.error("Plan Id is not specified.");
    }

    return props.getPlanLogs(props.planId)
      .then((response: any) => {
        setNoteLogsOneDataSource(response.RmiPacpHeaderLogs);
        setNoteLogsTwoDataSource(response.RmiPacpAccessLogs);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message ||"Something went wrong, please contact a system administrator.");
      });
  };

  const getDetailPlan = () => {
    if (props.planId === 0) { //planId is 0 then it's creating a new plan
      return;
    } else {
      return props.getDetailPlan(props.planId, sessionStorage.user_id)
        .then((response: any[]) => {
          setDescription(props.pacPlan.RmiDescription);
          setDischargeSummary(props.pacPlan.RmiDischargeSummary);
          setReconciliation(props.pacPlan.RmiMedReconciliation);
          setNoteBy(props.pacPlan.RmiNoteBy);
          setNoteDateTime(formatDate(props.pacPlan.RmiNoteDate, 'MM/DD/YYYY hh:mm'));
          setMostRecentHospitalizeDate(formatDate(props.pacPlan.RmiMostRecentHospDate, 'MM/DD/YYYY'));
          setStatus(props.status);
          setCarePlan(response);
        })
        .catch((err: ErrorResponse) => {
          notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
        });
    }
  }

  //create new and edit plan uses the same save api
  const savePlan = () => {
    const formData = {
      'CompanyId': props.eligibilityMemberInfo.RmiCompanyId,
      'CreatedBy': props.pacPlan.RmiCreatedBy ? props.pacPlan.RmiCreatedBy : sessionStorage.user_id,
      'CreatedDate': props.pacPlan.RmiCreatedDate ? props.pacPlan.RmiCreatedDate : moment(noteDateTime).toISOString(),
      'Description':  _.escape(description),
      'DischargeDate': null, //This is probably not used at all from the front-end on this page
      'DischargeSummary': _.escape(dischargeSummary),
      'MedReconciliation':  _.escape(reconciliation),
      'MembMpiNo': props.eligibilityMemberInfo.RmiMembMpiNo,
      'MemberId': props.eligibilityMemberInfo.RmiMembId,
      'ModifiedBy': sessionStorage.user_id,
      'ModifiedDate': moment().toISOString(),
      'MostRecentHospDate': mostRecentHospitalizeDate,
      'NoteBy': noteBy,
      'NoteDate': moment(noteDateTime).toISOString(),
      'PacpAssessment': carePlan,
      'PacpMedication': pacpMedication,
      'PlanId': props.planId,
      'PlanNo': props.pacPlan.RmiPlanNo,
      'StatusId': getEnumKeyByEnumValue(PacStatus, status)
    }

    return props.savePlan(formData)
      .then(() => {
        notifier.success('Saved');
        goBack();
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const openPlanLogModal = () => {
    getPlanLogs();
    setIsPlanLogVisible(true)
  }

  const closePlanLogModal = () => {
    setIsPlanLogVisible(false);
  }

  const print = () => {
    $(".print-logo").removeClass("exclude-print").addClass("include-print");
    window.print();
  }

  const goBack = () => {
    //reset these fields just case
    setNoteDateTime('');
    setMostRecentHospitalizeDate('');
    setStatus('');
    setNoteBy('');
    setDischargeSummary('');
    setDescription('');
    setReconciliation('');

    history.push({
      pathname: '/eligibility/member-detail',
      state: {
        eligibilityMemberInfo: props.eligibilityMemberInfo
      }
    });
  }

  return (
    <>
      <div className="printClinicalPlan mt-4">
        <hr />
        <PageHeader
          className="site-page-header"
          onBack={() => goBack()}
          title="Post Acute Care Clinical Plan"
          extra={[
            <Button
              className="print-button"
              type="primary"
              key="print"
              onClick={print}
              icon={<PrinterOutlined />}
            >
              Print
            </Button>,
          ]}
        >
          <Descriptions size="small" column={3}>
            <Descriptions.Item className="member-name" label="Member Name">
              {props.eligibilityMemberInfo.RmiRevFullName}
            </Descriptions.Item>
            <Descriptions.Item className="date-of-birth" label="Date of Birth">
              {formatDate(props.eligibilityMemberInfo.RmiBirth, "MM/DD/YYYY")}
            </Descriptions.Item>
            <Descriptions.Item className="pcp" label="PCP">
              {props.primaryCareProvider.RmiRevFullName}
            </Descriptions.Item>
            <Descriptions.Item className="gender" label="Gender">
              {" "}
              {props.eligibilityMemberInfo.RmiSex}
            </Descriptions.Item>
            <Descriptions.Item className="age" label="Age">
              {moment().diff(props.eligibilityMemberInfo.RmiBirth, "years")}
            </Descriptions.Item>
          </Descriptions>
        </PageHeader>

        <hr />

        <Form
          form={form}
          name='postAcuteCarePlanForm'
          onFinish={savePlan}
        >
          <div className="row flex-lg-wrap mb-3 ml-2 mr-2 mt-3">
            <div className="col-lg-24 col-md-24 mb-4">
              <table className="plan-row-table" style={{width: '100%', borderCollapse: 'separate', borderSpacing: '15px 0'}}>
                <tbody>
                  <tr>
                    <td className="break">
                      <span className="plan-label">Plan No: </span>
                      <span className="plan-description">{props.pacPlan.RmiPlanNo}</span>
                    </td>
                    <td className="break">
                      <span className="plan-label">Note By: </span>
                      <span className="plan-description">
                        <Input value={noteBy}
                          onChange={e => {
                            const currValue = e.target.value;
                            setNoteBy(currValue);
                          }}
                          disabled={sessionStorage.user_role !== UserRole.CASE} />
                      </span>
                    </td>
                    <td className="break">
                      <span className="plan-label">Note Date & Time: (MM/DD/YYYY hh:mm) </span>
                      <span className="plan-description">
                        <DatePickerLocalised
                          date={noteDateTime}
                          setDate={setNoteDateTime}
                          formInstance={form}
                          formItemName={'noteDateTime'}
                          showTime={true}
                          disabled={sessionStorage.user_role !== UserRole.CASE}
                        />
                      </span>
                    </td>
                    <td className="break">
                      <span className="plan-label">Most Recent Hosp: (MM/DD/YYYY) </span>
                      <span className="plan-description">
                        <DatePickerLocalised
                          date={mostRecentHospitalizeDate}
                          setDate={setMostRecentHospitalizeDate}
                          formInstance={form}
                          formItemName={'mostRecentHospitalizeDate'}
                          disabled={sessionStorage.user_role !== UserRole.CASE}
                        />
                      </span>
                    </td>
                    <td className="break">
                      <span className="plan-label">Status: </span>
                      <span className="plan-description">
                        <Select
                          value={status}
                          placeholder="Select a status"
                          onChange={(value) => setStatus(value)}
                          disabled={sessionStorage.user_role !== UserRole.CASE}
                          style={{width: '100%'}}
                        >
                          <Option key={1} value={PacStatus[1]}>
                            OPEN
                          </Option>
                          <Option key={2} value={PacStatus[2]}>
                            PENDING
                          </Option>
                          <Option key={3} value={PacStatus[3]}>
                            COMPLETED
                          </Option>
                        </Select>
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="mt-3 mb-4">
                <label className="editor-label">
                  History & Physical/Last Discharge Summary
                </label>
                <ReactQuill
                  className="mt-2 ql-bubble-box"
                  theme={sessionStorage.user_role !== UserRole.CASE ? "bubble" : "snow"}
                  readOnly={sessionStorage.user_role !== UserRole.CASE}
                  modules={modules}
                  formats={formats}
                  value={_.unescape(dischargeSummary)}
                  onChange={(value) => setDischargeSummary(value)}
                />
              </div>

              <div className="mb-4">
                <label className="editor-label">Description</label>
                <ReactQuill
                  className="mt-2 ql-bubble-box"
                  theme={sessionStorage.user_role !== UserRole.CASE ? "bubble" : "snow"}
                  readOnly={sessionStorage.user_role !== UserRole.CASE}
                  modules={modules}
                  formats={formats}
                  value={_.unescape(description)}
                  onChange={(value) => setDescription(value)}
                />
              </div>

              <div className="mb-4">
                <Table columns={columns} dataSource={carePlan} rowKey="RmiSequence" pagination={{ position: ["bottomCenter"],  hideOnSinglePage: true}} />
              </div>

              <div className="mb-4">
                <label className="editor-label">Medication Reconciliation</label>
                <ReactQuill
                  className="ql-bubble-box mt-2"
                  theme={sessionStorage.user_role !== UserRole.CASE ? "bubble" : "snow"}
                  readOnly={sessionStorage.user_role !== UserRole.CASE}
                  modules={modules}
                  formats={formats}
                  value={_.unescape(reconciliation)}
                  onChange={(value) => setReconciliation(value)}
                />
              </div>

              { sessionStorage.user_role === UserRole.CASE &&
                <>
                  <Button className="mr-1" onClick={savePlan}>Save</Button>
                  <Button onClick={openPlanLogModal} icon={<SearchOutlined />}>Log</Button>
                </>
              }
            </div>
          </div>
        </Form>
      </div>
      <Modal
        visible={isPlanLogVisible}
        destroyOnClose={true}
        maskClosable={false}
        width={1400}
        onCancel={closePlanLogModal}
        footer={null}
      >
        <Title className="text-center" level={3}>Post Acute Care Plan Logs</Title>
        <label className="modal-label">Changes to</label>
        <Table
          columns={noteLogColumnsOne}
          dataSource={noteLogsOneDataSource}
          pagination={{ position: ["bottomCenter"],  hideOnSinglePage: true}}
          loading={props.isFetchingPlanLogs}
        />
        <label className="modal-label">Acute Care Plan Access</label>
        <Table
          columns={noteLogColumnsTwo}
          dataSource={noteLogsTwoDataSource}
          pagination={{ position: ["bottomCenter"],  hideOnSinglePage: true}}
          loading={props.isFetchingPlanLogs}
        />
      </Modal>
    </>
  );
};

export default PostAcuteCareClinicalPlanDetail;
